/**
 * Advanced ChatBot Component
 * ChatBot with advanced features inspired by Welly
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import * as styles from './ChatBot.css';
import type { 
  ChatSettings, 
  ChatSize, 
  FontSize, 
  ThemeMode, 
  ChatMessage, 
  QuickAction
} from './types';
import {
  CHAT_SIZES, 
  FONT_SIZES, 
  QUICK_ACTIONS 
} from './types';

// Icons (you can replace with your preferred icons)
const Icons = {
  chat: '💬',
  close: '✕',
  settings: '⚙️',
  size: '📐',
  menu: '☰',
  restart: '🔄',
  send: '📤',
  chevronRight: '▶',
  moon: '🌙',
  sun: '☀️',
  auto: '🔄'
};
interface ChatBotProps {
  apiUrl?: string;
  title?: string;
  subtitle?: string;
  initialMessages?: string[];
}

export const ChatBot: React.FC<ChatBotProps> = ({
  apiUrl = '/api/v1/chatbot/message', // Nouveau backend FastAPI / RAG
  title = '👻 Jef — Assistant IA',
  subtitle = 'Posez vos questions sur mon parcours et mes projets',
  initialMessages = []
}) => {
  // Security: simple limits and anti-spam
  const MAX_MESSAGE_LENGTH = 500;
  const SEND_COOLDOWN_MS = 800;
  // Racine du portail pour s'assurer que le positionnement n'est pas affecté par des parents avec transform/isolation
  const [portalEl, setPortalEl] = useState<HTMLElement | null>(null);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    let el = document.getElementById('chatbot-portal-root');
    if (!el) {
      el = document.createElement('div');
      el.id = 'chatbot-portal-root';
      document.body.appendChild(el);
    }
    setPortalEl(el);
  }, []);
  // Lightweight (safe) Markdown formatting for rendering
  const formatMarkdownLite = (raw: string) => {
    const esc = (s: string) =>
      s
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
    let text = esc(raw);
  // Links: [label](url)
    text = text.replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, (_m, label, url) => {
  // Validate protocol and sanitize the URL (attribute)
      try {
        const u = new URL(url);
        if (u.protocol !== 'http:' && u.protocol !== 'https:') return label;
      } catch {
        return label;
      }
      const safeUrl = String(url).replace(/"/g, '&quot;').replace(/'/g, '&#39;');
      return `<a href="${safeUrl}" target="_blank" rel="noopener noreferrer">${label}</a>`;
    });
  // Bold: **bold**
    text = text.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  // Italic: *italic*
    text = text.replace(/(^|\s)\*([^*]+)\*(?=\s|$)/g, '$1<em>$2</em>');
  // Bullets: lines starting with - or •
    const lines = text.split(/\r?\n/);
    let inList = false;
    const out: string[] = [];
    for (const line of lines) {
      if (/^\s*(?:[-•])\s+/.test(line)) {
        if (!inList) {
          out.push('<ul>');
          inList = true;
        }
        out.push('<li>' + line.replace(/^\s*(?:[-•])\s+/, '') + '</li>');
      } else {
        if (inList) {
          out.push('</ul>');
          inList = false;
        }
  // Line breaks
        out.push(line.replace(/\n/g, '<br/>'));
      }
    }
    if (inList) out.push('</ul>');
    return out.join('\n');
  };
  // State
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showSizeDropdown, setShowSizeDropdown] = useState(false);
  const [showQuickMenu, setShowQuickMenu] = useState(false);
  const [isOfflineMode, setIsOfflineMode] = useState(false);
  const [fallbackNotificationShown, setFallbackNotificationShown] = useState(false);
  const lastSendRef = useRef<number>(0);
  // Identifiant de session stable : conservé pour toute la conversation afin que
  // le backend puisse en garder l'historique (continuité du dialogue).
  const sessionIdRef = useRef<string>(
    `session-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
  );
  
  // Settings
  const [settings, setSettings] = useState<ChatSettings>({
    size: 'medium',
    fontSize: 'medium',
    theme: 'auto',
    suggestions: true,
    sound: false,
    animations: true
  });
  
  // Settings sections state
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    display: false,
    notifications: false,
    advanced: false
  });
  
  // Refs
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const chatWindowRef = useRef<HTMLDivElement>(null);
  
  // Utilities
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);
  
  const closeAllDropdowns = useCallback(() => {
    setShowSettings(false);
    setShowSizeDropdown(false);
    setShowQuickMenu(false);
  }, []);
  
  // Settings management
  const updateSetting = useCallback(<K extends keyof ChatSettings>(
    key: K, 
    value: ChatSettings[K]
  ) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  // Save to localStorage
    localStorage.setItem('chatbot-settings', JSON.stringify({ ...settings, [key]: value }));
  }, [settings]);
  
  // Load settings from localStorage
  useEffect(() => {
    const savedSettings = localStorage.getItem('chatbot-settings');
    if (savedSettings) {
      try {
        const parsed = JSON.parse(savedSettings);
        setSettings(parsed);
      } catch (error) {
        }
    }
  }, []);
  
  // Initial messages
  useEffect(() => {
    if (initialMessages.length > 0 && messages.length === 0) {
      const welcomeMessages: ChatMessage[] = initialMessages.map((text, index) => ({
        id: `welcome-${index}`,
        text,
        sender: 'bot',
        timestamp: new Date()
      }));
      setMessages(welcomeMessages);
    }
  }, [initialMessages, messages.length]);
  
  // Auto-scroll
  useEffect(() => {
    if (settings.animations) {
      scrollToBottom();
    }
  }, [messages, scrollToBottom, settings.animations]);
  
  // Close dropdowns when clicking elsewhere (including inside chat but outside menus)
  useEffect(() => {
    const handleDocPointer = (event: MouseEvent) => {
      const chatEl = chatWindowRef.current;
      if (!chatEl) return;
      const target = event.target as Node;

      const insideChat = chatEl.contains(target);

  // If clicking inside a dropdown wrapper (button + content), do nothing to avoid conflicts
      const dropdownWrappers = chatEl.querySelectorAll(`.${styles.dropdown}`);
      let insideAnyDropdownWrapper = false;
      dropdownWrappers.forEach((el) => {
        if (el.contains(target)) insideAnyDropdownWrapper = true;
      });
      if (insideAnyDropdownWrapper) return;

  // Check if click is inside an active menu
      const activeDropdowns = chatEl.querySelectorAll(`.${styles.dropdownContent}.active`);
      let insideActiveDropdown = false;
      activeDropdowns.forEach((el) => {
        if (el.contains(target)) insideActiveDropdown = true;
      });

  // Outside chat: close. Inside chat but outside active menu: close.
      if (!insideChat || (insideChat && !insideActiveDropdown)) {
        closeAllDropdowns();
      }
    };

    if (showSettings || showSizeDropdown || showQuickMenu) {
      document.addEventListener('mousedown', handleDocPointer);
      return () => document.removeEventListener('mousedown', handleDocPointer);
    }
  }, [showSettings, showSizeDropdown, showQuickMenu, closeAllDropdowns]);

  // Close chat on outside click and Escape key
  useEffect(() => {
    if (!isOpen) return;
    const onMouseDown = (e: MouseEvent) => {
      if (chatWindowRef.current && !chatWindowRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        closeAllDropdowns();
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
        closeAllDropdowns();
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen, closeAllDropdowns]);
  
  // Réponses de secours (mode hors-ligne) : même voix que le backend — je parle
  // à la première personne en tant que Jean-François, vouvoiement, phrases courtes
  // qui invitent à poursuivre le dialogue, sans emoji.
  const getFallbackResponse = useCallback((text: string): string => {
    const lowerText = text.toLowerCase();

    // Salutations
    if (/^(salut|bonjour|hello|coucou|yo|hey)\b/i.test(lowerText)) {
      return "Bonjour ! Je suis Jean-François. Vous voulez que je vous parle de mon parcours, de mes projets ou de mes compétences ?";
    }

    // À propos / parcours
    if (/\b(à propos|parcours|profil|qui|toi|vous)\b/i.test(lowerText)) {
      return "Je suis Jean-François Chaussoy, développeur web fullstack et data scientist, actuellement en alternance Data & IA. Souhaitez-vous en savoir plus sur mon parcours ou mes projets ?";
    }

    // Compétences
    if (/\b(compétences|technologies|skills|stack|langage|outils)\b/i.test(lowerText)) {
      return "Je travaille surtout en React/TypeScript côté front et Node.js côté back, avec une vraie appétence pour la data et l'IA. Une techno en particulier vous intéresse ?";
    }

    // Projets
    if (/\b(projets?|réalisations?|portfolio|travaux?|créations?)\b/i.test(lowerText)) {
      return "J'ai plusieurs projets sur ce portfolio, du web fullstack à la data. Voulez-vous que je vous en présente un en particulier ?";
    }

    // Contact
    if (/\b(contact|email|linkedin|github|embauche|collaboration|recrut|travail)\b/i.test(lowerText)) {
      return "Je suis ouvert aux échanges et opportunités. Le plus simple est le formulaire de contact du portfolio, ou mes liens LinkedIn et GitHub dans le pied de page. De quoi aimeriez-vous parler ?";
    }

    // Formation / expérience
    if (/\b(formation|études|expérience|diplôme|université|école|alternance)\b/i.test(lowerText)) {
      return "Mon parcours mêle formation technique et projets concrets, et aujourd'hui je suis en alternance Data & IA. Voulez-vous que je détaille mes expériences ou mes formations ?";
    }

    // Aide
    if (/\b(aide|comment|utiliser|fonction|marche|help)\b/i.test(lowerText)) {
      return "Posez-moi vos questions sur mon parcours, mes compétences ou mes projets, et le menu rapide propose aussi quelques questions toutes prêtes. Par quoi souhaitez-vous commencer ?";
    }

    // Remerciements
    if (/\b(merci|thanks|super|cool|génial|parfait|excellent)\b/i.test(lowerText)) {
      return "Avec plaisir ! Avez-vous d'autres questions sur mon parcours ou mes projets ?";
    }

    // Technologies précises
    if (/\b(react|typescript|javascript|node|css|html|git|python|data|ia)\b/i.test(lowerText)) {
      return "Oui, ce sont des outils avec lesquels je travaille au quotidien. Voulez-vous un exemple concret tiré de mes projets ?";
    }

    // Réponse par défaut, courte et orientée dialogue
    const responses = [
      "Bonne question ! Je peux vous parler de mon parcours, de mes compétences ou de mes projets. Qu'est-ce qui vous intéresse ?",
      "Je préfère rester sur ce que je connais : mon parcours, mes projets et mes compétences. Sur quel point puis-je vous éclairer ?",
      "Avec plaisir d'en discuter. Souhaitez-vous que je vous parle de mes projets, de mes compétences ou de mon parcours ?",
    ];
    return responses[Math.floor(Math.random() * responses.length)];
  }, []);

  // Send message prioritizing the original n8n workflow
  const sendMessage = useCallback(async (text: string) => {
    const now = Date.now();
    if (!text.trim()) return;
  // Anti-spam: cooldown between sends
    if (now - lastSendRef.current < SEND_COOLDOWN_MS) return;
  // Limit length
    const trimmed = text.trim().slice(0, MAX_MESSAGE_LENGTH);
    lastSendRef.current = now;
    
    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      text: trimmed,
      sender: 'user',
      timestamp: new Date()
    };
    
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsTyping(true);
    
    try {
  // PRIORITY attempt with the original n8n workflow via secure proxy
    const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ 
      message: trimmed,
          session_id: sessionIdRef.current
        })
      });
      
      if (response.ok) {
        const data = await response.json();

  // Conserve l'identifiant de session renvoyé par le serveur (continuité)
        if (data.session_id) {
          sessionIdRef.current = data.session_id;
        }

  // Handle response from our secure proxy API
        let botText = '';
        if (data.response) {
          botText = data.response;
        } else if (data.data?.message) {
          botText = data.data.message;
        } else if (data.fallback?.message) {
          botText = data.fallback.message;
          // Switch to offline mode if using fallback
          if (!isOfflineMode) {
            setIsOfflineMode(true);
          }
        } else {
          throw new Error('Format de réponse API invalide');
        }
        
        const botMessage: ChatMessage = {
          id: `bot-${Date.now()}`,
          text: botText,
          sender: 'bot',
          timestamp: new Date()
        };
        
        setMessages(prev => [...prev, botMessage]);
        
  // Reset offline mode if API works
        if (isOfflineMode) {
          setIsOfflineMode(false);
          }
        
      } else {
        throw new Error(`API proxy indisponible (${response.status})`);
      }
    } catch (error) {
  // Enable offline mode ONLY if API fails
      if (!isOfflineMode) {
        setIsOfflineMode(true);
        
  // Explain fallback via notification
        if (!fallbackNotificationShown) {
          setFallbackNotificationShown(true);
          setTimeout(() => {
            const notificationMessage: ChatMessage = {
              id: `notification-${Date.now()}`,
              text: "⚠️ Connexion au chatbot temporairement indisponible. Basculement en mode local... 🔄",
              sender: 'bot',
              timestamp: new Date()
            };
            setMessages(prev => [...prev, notificationMessage]);
          }, 500);
        }
      }
      
  // Fallback mode with smart responses (last resort)
    setTimeout(() => {
        const fallbackMessage: ChatMessage = {
          id: `fallback-${Date.now()}`,
      text: getFallbackResponse(trimmed),
          sender: 'bot',
          timestamp: new Date()
        };
        setMessages(prev => [...prev, fallbackMessage]);
        setIsTyping(false);
      }, 800 + Math.random() * 1200);
      
  return; // Exit early to avoid finally
    }
    
    setIsTyping(false);
  }, [apiUrl, getFallbackResponse, isOfflineMode, fallbackNotificationShown]);
  
  // Input handling
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
  const next = e.target.value;
  setInputValue(next.length > MAX_MESSAGE_LENGTH ? next.slice(0, MAX_MESSAGE_LENGTH) : next);
  }, []);
  
  const handleInputKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(inputValue);
    }
  }, [inputValue, sendMessage]);
  
  // Quick actions
  const handleQuickAction = useCallback((action: QuickAction) => {
    sendMessage(action.message);
    setShowQuickMenu(false);
  }, [sendMessage]);
  
  // Restart conversation
  const restartConversation = useCallback(() => {
    setMessages([]);
    // Nouvelle session : le backend repart d'un historique vierge
    sessionIdRef.current = `session-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    if (initialMessages.length > 0) {
      const welcomeMessages: ChatMessage[] = initialMessages.map((text, index) => ({
        id: `welcome-${index}`,
        text,
        sender: 'bot',
        timestamp: new Date()
      }));
      setMessages(welcomeMessages);
    }
    closeAllDropdowns();
  }, [initialMessages, closeAllDropdowns]);
  
  // Manage settings sections
  const toggleSection = useCallback((sectionId: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  }, []);
  
  // Group actions by category
  const actionsByCategory = QUICK_ACTIONS.reduce((acc, action) => {
    if (!acc[action.category]) {
      acc[action.category] = [];
    }
    acc[action.category].push(action);
    return acc;
  }, {} as Record<string, QuickAction[]>);
  
  // Suggestions intelligentes et contextuelles
  const getSuggestions = useCallback(() => {
    if (!settings.suggestions || messages.length === 0) return [];
    
    const lastMessage = messages[messages.length - 1];
    if (lastMessage.sender === 'user') return [];
    
    const lastText = (lastMessage.text || '').toLowerCase();
    
    // Si le dernier message parlait déjà de projets ou de technologies
    if (lastText.includes('projet') || lastText.includes('rag') || lastText.includes('kestra') || lastText.includes('modèle')) {
      return [
        'Parle-moi de ton alternance à la CAB',
        'Quelles sont tes technologies clés ?',
        'Comment puis-je te contacter ?'
      ];
    }
    
    // Si le dernier message parlait de son alternance ou de sa formation
    if (lastText.includes('alternance') || lastText.includes('boulonnais') || lastText.includes('openclassrooms') || lastText.includes('formation')) {
      return [
        'Quels sont tes projets en Data & IA ?',
        'Présente-moi le Projet 13 (RAG)',
        'Comment puis-je te contacter ?'
      ];
    }

    // Suggestions d'accueil et d'ouverture par défaut
    return [
      'Quels sont tes projets en Data & IA ?',
      'Parle-moi de ton alternance à la CAB',
      'Quelles sont tes compétences clés ?'
    ];
  }, [messages, settings.suggestions]);
  
  const chatUI = (
    <div className={styles.chatContainer}>
      <div 
        ref={chatWindowRef}
        className={`${styles.chatWindow} ${styles.chatWindowSizes[settings.size]}`}
        style={{ fontSize: FONT_SIZES[settings.fontSize].size }}
      >
        {/* Header */}
        <div className={styles.chatHeader}>
          <div className={styles.headerInfo}>
            <h3 className={styles.headerTitle}>{title}</h3>
            <p className={styles.headerSubtitle}>
              {isOfflineMode ? '⚠️ Mode local (Assistant IA indisponible)' : subtitle}
            </p>
          </div>
          
          <div className={styles.headerControls}>
            {/* Quick access menu */}
            <div className={styles.dropdown}>
              <button
                className={styles.headerButton}
                onClick={() => {
                  setShowQuickMenu(!showQuickMenu);
                  setShowSettings(false);
                  setShowSizeDropdown(false);
                }}
                aria-label="Quick access menu"
              >
                {Icons.menu}
              </button>
              
              <div className={`${styles.dropdownContent} ${showQuickMenu ? 'active' : ''}`}>
                <div className={styles.quickMenu}>
                  {Object.entries(actionsByCategory).map(([category, actions]) => (
                    <div key={category} className={styles.menuCategory}>
                      <div className={styles.menuCategoryTitle}>
                        {category}
                      </div>
                      {actions.map(action => (
                        <button
                          key={action.id}
                          className={styles.menuItem}
                          onClick={() => handleQuickAction(action)}
                        >
                          <span className={styles.menuItemIcon}>{action.icon}</span>
                          <span className={styles.menuItemText}>{action.label}</span>
                        </button>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            {/* Size control */}
            <div className={styles.dropdown}>
              <button
                className={styles.headerButton}
                onClick={() => {
                  setShowSizeDropdown(!showSizeDropdown);
                  setShowSettings(false);
                  setShowQuickMenu(false);
                }}
                aria-label="Change size"
              >
                {Icons.size}
              </button>
              
              <div className={`${styles.dropdownContent} ${showSizeDropdown ? 'active' : ''}`}>
                {Object.entries(CHAT_SIZES).map(([size, config]) => (
                  <div
                    key={size}
                    className={`${styles.sizeOption} ${settings.size === size ? 'active' : ''}`}
                    onClick={() => {
                      updateSetting('size', size as ChatSize);
                      setShowSizeDropdown(false);
                    }}
                  >
                    <span className={styles.sizeOptionIcon}>{config.icon}</span>
                    <div>
                      <div className={styles.sizeOptionText}>{config.label}</div>
                      <div className={styles.sizeOptionDesc}>{config.description}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Settings */}
            <div className={styles.dropdown}>
              <button
                className={styles.headerButton}
                onClick={() => {
                  setShowSettings(!showSettings);
                  setShowSizeDropdown(false);
                  setShowQuickMenu(false);
                }}
                aria-label="Settings"
              >
                {Icons.settings}
              </button>
              
              <div className={`${styles.dropdownContent} ${showSettings ? 'active' : ''}`}>
                {/* Display section */}
                <div className={styles.settingsSection}>
                  <button
                    className={styles.settingsHeader}
                    onClick={() => toggleSection('display')}
                  >
                    <h4 className={styles.settingsTitle}>
                      🎨 Affichage
                    </h4>
                    <span className={`${styles.settingsArrow} ${expandedSections.display ? 'expanded' : ''}`}>
                      {Icons.chevronRight}
                    </span>
                  </button>
                  
                  <div className={`${styles.settingsContent} ${expandedSections.display ? 'expanded' : ''}`}>
                    {/* Font size */}
                    <div className={styles.controlGroup}>
                      <div className={styles.toggleInfo}>
                        <div className={styles.toggleLabel}>Taille de police</div>
                        <div className={styles.toggleDescription}>
                          Ajustez la taille du texte pour une meilleure lisibilité
                        </div>
                      </div>
                      <div className={styles.buttonRow}>
                        {Object.entries(FONT_SIZES).map(([size, config]) => (
                          <button
                            key={size}
                            onClick={() => updateSetting('fontSize', size as FontSize)}
                            style={{
                              padding: '4px 8px',
                              border: `1px solid ${settings.fontSize === size ? '#64ffda' : '#2d3952'}`,
                              background: settings.fontSize === size ? '#64ffda' : 'transparent',
                              color: settings.fontSize === size ? '#0a192f' : '#ccd6f6',
                              borderRadius: '4px',
                              fontSize: '0.75rem',
                              cursor: 'pointer'
                            }}
                          >
                            {config.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Notifications section */}
                <div className={styles.settingsSection}>
                  <button
                    className={styles.settingsHeader}
                    onClick={() => toggleSection('notifications')}
                  >
                    <h4 className={styles.settingsTitle}>
                      🔔 Notifications
                    </h4>
                    <span className={`${styles.settingsArrow} ${expandedSections.notifications ? 'expanded' : ''}`}>
                      {Icons.chevronRight}
                    </span>
                  </button>
                  
                  <div className={`${styles.settingsContent} ${expandedSections.notifications ? 'expanded' : ''}`}>
                    <div className={styles.toggleSwitch}>
                      <div className={styles.toggleInfo}>
                        <div className={styles.toggleLabel}>Sons</div>
                        <div className={styles.toggleDescription}>
                          Enable sound notifications
                        </div>
                      </div>
                      <div
                        className={`${styles.toggleControl} ${settings.sound ? 'active' : ''}`}
                        onClick={() => updateSetting('sound', !settings.sound)}
                      />
                    </div>
                    
                    <div className={styles.toggleSwitch}>
                      <div className={styles.toggleInfo}>
                        <div className={styles.toggleLabel}>Suggestions</div>
                        <div className={styles.toggleDescription}>
                          Show quick reply suggestions
                        </div>
                      </div>
                      <div
                        className={`${styles.toggleControl} ${settings.suggestions ? 'active' : ''}`}
                        onClick={() => updateSetting('suggestions', !settings.suggestions)}
                      />
                    </div>
                  </div>
                </div>
                
                {/* Advanced section */}
                <div className={styles.settingsSection}>
                  <button
                    className={styles.settingsHeader}
                    onClick={() => toggleSection('advanced')}
                  >
                    <h4 className={styles.settingsTitle}>
                      ⚡ Avancé
                    </h4>
                    <span className={`${styles.settingsArrow} ${expandedSections.advanced ? 'expanded' : ''}`}>
                      {Icons.chevronRight}
                    </span>
                  </button>
                  
                  <div className={`${styles.settingsContent} ${expandedSections.advanced ? 'expanded' : ''}`}>
                    <div className={styles.toggleSwitch}>
                      <div className={styles.toggleInfo}>
                        <div className={styles.toggleLabel}>Animations</div>
                        <div className={styles.toggleDescription}>
                          Enable animations and transitions
                        </div>
                      </div>
                      <div
                        className={`${styles.toggleControl} ${settings.animations ? 'active' : ''}`}
                        onClick={() => updateSetting('animations', !settings.animations)}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Restart button */}
            <button
              className={styles.headerButton}
              onClick={restartConversation}
              aria-label="Restart conversation"
            >
              {Icons.restart}
            </button>
            
            {/* Close button */}
            <button
              className={styles.headerButton}
              onClick={() => setIsOpen(false)}
              aria-label="Close chat"
            >
              {Icons.close}
            </button>
          </div>
        </div>
        
  {/* Messages */}
        <div className={styles.messagesContainer}>
          {messages.map(message => (
            <div
              key={message.id}
              className={message.sender === 'user' ? styles.messageUser : styles.messageBot}
            >
              {/* Basic Markdown: **bold**, *italic*, lists, links */}
              <div
                className={styles.messageContent}
                dangerouslySetInnerHTML={{
                  __html: formatMarkdownLite(message.text)
                }}
              />
            </div>
          ))}
          
          {isTyping && (
            <div className={styles.typingIndicator}>
              <div className={styles.typingDot} />
              <div className={styles.typingDot} />
              <div className={styles.typingDot} />
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>
        
  {/* Suggestions */}
        {settings.suggestions && getSuggestions().length > 0 && (
          <div className={styles.suggestionsContainer}>
            {getSuggestions().map((suggestion, index) => (
              <button
                key={index}
                className={styles.suggestionChip}
                onClick={() => sendMessage(suggestion)}
              >
                {suggestion}
              </button>
            ))}
          </div>
        )}
        
  {/* Input area */}
        <div className={styles.inputContainer}>
          <textarea
            ref={inputRef}
            className={styles.messageInput}
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleInputKeyDown}
            placeholder="Tapez votre message..."
            rows={1}
            style={{
              fontSize: FONT_SIZES[settings.fontSize].size,
              resize: 'none'
            }}
          />
          <button
            className={styles.sendButton}
            onClick={() => sendMessage(inputValue)}
            disabled={!inputValue.trim() || isTyping}
            aria-label="Send message"
          >
            {Icons.send}
          </button>
        </div>
      </div>
    </div>
  );
  
  const toggleUI = (
    <button
      className={styles.toggleButton}
      onClick={() => setIsOpen(true)}
      aria-label="Ouvrir le chat"
    >
      {Icons.chat}
    </button>
  );

  // Rendu via un portail pour éviter les problèmes de z-index ou de transform de l'ancêtre
  if (portalEl) {
    return createPortal(isOpen ? chatUI : toggleUI, portalEl);
  }
  
  return null;
};

export default ChatBot;